import { useTranslation } from 'react-i18next';
import { Archive, CheckCircle } from 'lucide-react';
import type { Notification, NotificationReadFilter } from '../../models/Notification.types';
import { formatNotificationDateLabel } from '../../utils/formatNotificationDateTime';

interface NotificationItemProps {
  notification: Notification;
  readFilter: NotificationReadFilter;
  onOpen: (notification: Notification) => void;
  onMarkAsReadOnly: (id: number) => void;
  onArchive: (id: number) => void;
}

export const NotificationItem = ({
  notification,
  readFilter,
  onOpen,
  onMarkAsReadOnly,
  onArchive,
}: NotificationItemProps) => {
  const { t, i18n } = useTranslation('notificationsModule');
  const isRead = notification.read_at !== null;
  const lang = i18n.language?.startsWith('es') ? 'es' : 'en';

  const dateLabel = formatNotificationDateLabel(notification.created_at, lang, t);

  const getTitle = () => {
    if (notification.title) {
      return notification.title;
    }
    if (notification.i18n_key) {
      return t(notification.i18n_key, notification.data as Record<string, string>);
    }
    return '';
  };

  const getDescription = () => {
    if (notification.description) {
      return notification.description;
    }
    if (notification.i18n_key) {
      return t(`${notification.i18n_key}_description`, notification.data as Record<string, string>);
    }
    return '';
  };

  const handleOpen = () => {
    onOpen(notification);
  };

  const handleMarkRead = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isRead) {
      onMarkAsReadOnly(notification.id);
    }
  };

  const handleArchive = (e: React.MouseEvent) => {
    e.stopPropagation();
    onArchive(notification.id);
  };

  const showArchive = readFilter === 'read' && isRead;

  return (
    <div
      className={`
        flex gap-1 rounded px-1 py-0.5 transition-colors
        hover:bg-hover-press
        ${!isRead ? 'bg-card-bg' : ''}
      `}
      data-testid={`notification-item-${notification.id}`}
    >
      <button
        type="button"
        onClick={handleOpen}
        className={`
          flex-1 min-w-0 px-2 py-2 flex flex-col gap-2 cursor-pointer text-left rounded
          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background
        `}
      >
        <div className="flex items-start justify-between gap-2 min-w-0 w-full">
          <div className="flex items-start gap-2 min-w-0 flex-1">
            {!isRead && (
              <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-1.5" />
            )}
            <span className="text-text text-sm font-bold leading-5 break-words">
              {getTitle()}
            </span>
          </div>
          {dateLabel ? (
            <span className="text-subtext text-[10px] leading-4 shrink-0 whitespace-nowrap pt-0.5">
              {dateLabel}
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-1">
          <span className="text-subtext text-xs font-normal leading-4">
            {getDescription()}
          </span>
        </div>
      </button>

      <div className="flex flex-col gap-1 shrink-0 pt-1 pr-1">
        {!isRead && (
          <button
            type="button"
            onClick={handleMarkRead}
            className="p-1 rounded text-icons hover:text-text transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            title={t('notifications.actions.markAsRead')}
            data-testid={`mark-read-${notification.id}`}
          >
            <CheckCircle className="w-4 h-4" />
          </button>
        )}
        {showArchive && (
          <button
            type="button"
            onClick={handleArchive}
            className="p-1 rounded text-icons hover:text-text transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            title={t('notifications.actions.archive')}
            data-testid={`archive-${notification.id}`}
          >
            <Archive className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};
